// Shared product taxonomy — drives Header mega menu, Custom Shop sidebar,
// Catalog filters, and Admin Product Editor dropdowns.
//
// Schema rationale (faceted, NOT nested duplicate categories):
//   demographic  → who it's for: men | women | unisex | couple | kids
//   category     → garment / product family: tshirt | hoodie | accessories | drinkware | …
//   sub_category → optional finer split (Accessories → sneakers/wallet etc.)
//
// Header menu is a flat list of 11 top-level entries. Each entry resolves
// to a Catalog filter URL like /shop?demographic=men or /shop?category=accessories.

export const DEMOGRAPHICS = [
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
  { value: "unisex", label: "Unisex" },
  { value: "couple", label: "Couple" },
  { value: "kids", label: "Kids" },
];

// Master category catalogue — used by Admin form
export const CATEGORIES = [
  { value: "tshirt", label: "T-Shirt" },
  { value: "shirt", label: "Shirt" },
  { value: "hoodie", label: "Hoodie / Sweatshirt" },
  { value: "polo", label: "Polo" },
  { value: "tank", label: "Tank Top / Vest" },
  { value: "saree", label: "Saree" },
  { value: "kurta", label: "Kurta" },
  { value: "kurti", label: "Kurti" },
  { value: "salwar_kameez", label: "Salwar Kameez" },
  { value: "lehenga", label: "Lehenga" },
  { value: "sherwani", label: "Sherwani" },
  { value: "sports", label: "Sports Wear" },
  { value: "uniform", label: "Uniform" },
  { value: "corporate", label: "Corporate Wear" },
  { value: "accessories", label: "Accessories" },
  { value: "drinkware", label: "Drinkware (Mug / Bottle)" },
  { value: "lifestyle", label: "Lifestyle (Mousepad / Keychain)" },
  { value: "footwear", label: "Footwear" },
  { value: "bag", label: "Bag" },
];

// Sub-category map keyed by `category` value.
export const SUB_CATEGORIES = {
  accessories: [
    { value: "sneakers", label: "Sneakers" },
    { value: "sunglasses", label: "Sunglasses" },
    { value: "wallet", label: "Wallet" },
    { value: "handbag", label: "Handbag" },
    { value: "clutch", label: "Clutch" },
    { value: "cap", label: "Cap" },
    { value: "belt", label: "Belt" },
    { value: "watch", label: "Watch" },
    { value: "scarf", label: "Scarf / Stole" },
  ],
  drinkware: [
    { value: "mug", label: "Ceramic Mug" },
    { value: "magic_mug", label: "Magic Mug" },
    { value: "couple_mug", label: "Couple Mug" },
    { value: "bottle", label: "Bottle (Steel)" },
    { value: "sipper", label: "Sipper" },
    { value: "tumbler", label: "Tumbler" },
    { value: "flask", label: "Flask" },
  ],
  lifestyle: [
    { value: "mousepad", label: "Mousepad" },
    { value: "keychain", label: "Keychain" },
    { value: "phone_case", label: "Phone Case" },
    { value: "photo_frame", label: "Photo Frame" },
    { value: "cushion", label: "Cushion" },
    { value: "tote_bag", label: "Tote Bag" },
    { value: "diary", label: "Personalized Diary" },
  ],
  uniform: [
    { value: "school", label: "School" },
    { value: "corporate", label: "Corporate" },
    { value: "industrial", label: "Industrial" },
    { value: "medical", label: "Medical / Scrubs" },
    { value: "hospitality", label: "Hospitality / Chef" },
  ],
  sports: [
    { value: "jersey", label: "Jersey" },
    { value: "tracksuit", label: "Track Suit" },
    { value: "gym_wear", label: "Gym Wear" },
    { value: "shorts", label: "Shorts" },
  ],
  corporate: [
    { value: "formal_shirt", label: "Formal Shirt" },
    { value: "blazer", label: "Blazer" },
    { value: "tie", label: "Tie" },
    { value: "bulk_polo", label: "Bulk Polo" },
    { value: "corporate_gift", label: "Corporate Gift" },
  ],
  footwear: [
    { value: "sneakers", label: "Sneakers" },
    { value: "loafers", label: "Loafers" },
    { value: "sandals", label: "Sandals" },
    { value: "flats", label: "Flats" },
    { value: "heels", label: "Heels" },
  ],
};

// Header mega-menu definition — 11 top-level entries in user-requested order.
// Each section drives a column of links inside the dropdown panel.
export const MEGA_MENU = [
  {
    label: "Men's Wear",
    demographic: "men",
    columns: [
      {
        title: "Topwear",
        links: [
          { label: "T-Shirts", to: "/shop?demographic=men&category=tshirt" },
          { label: "Shirts", to: "/shop?demographic=men&category=shirt" },
          { label: "Hoodies", to: "/shop?demographic=men&category=hoodie" },
          { label: "Polos", to: "/shop?demographic=men&category=polo" },
          { label: "Tanks / Vests", to: "/shop?demographic=men&category=tank" },
        ],
      },
      {
        title: "Occasion",
        links: [
          { label: "Sports Wear", to: "/shop?demographic=men&category=sports" },
          { label: "Corporate", to: "/shop?demographic=men&category=corporate" },
          { label: "Uniforms", to: "/shop?demographic=men&category=uniform" },
        ],
      },
      {
        title: "Accessories",
        links: [
          { label: "Sneakers", to: "/shop?demographic=men&category=accessories&sub_category=sneakers" },
          { label: "Wallets", to: "/shop?demographic=men&category=accessories&sub_category=wallet" },
          { label: "Caps", to: "/shop?demographic=men&category=accessories&sub_category=cap" },
          { label: "Watches", to: "/shop?demographic=men&category=accessories&sub_category=watch" },
        ],
      },
    ],
  },
  {
    label: "Women's Wear",
    demographic: "women",
    columns: [
      {
        title: "Topwear",
        links: [
          { label: "T-Shirts", to: "/shop?demographic=women&category=tshirt" },
          { label: "Tops / Shirts", to: "/shop?demographic=women&category=shirt" },
          { label: "Hoodies", to: "/shop?demographic=women&category=hoodie" },
          { label: "Polos", to: "/shop?demographic=women&category=polo" },
        ],
      },
      {
        title: "Occasion",
        links: [
          { label: "Sports Wear", to: "/shop?demographic=women&category=sports" },
          { label: "Corporate", to: "/shop?demographic=women&category=corporate" },
          { label: "Uniforms", to: "/shop?demographic=women&category=uniform" },
        ],
      },
      {
        title: "Accessories",
        links: [
          { label: "Sunglasses", to: "/shop?demographic=women&category=accessories&sub_category=sunglasses" },
          { label: "Handbags", to: "/shop?demographic=women&category=accessories&sub_category=handbag" },
          { label: "Clutches", to: "/shop?demographic=women&category=accessories&sub_category=clutch" },
          { label: "Scarves", to: "/shop?demographic=women&category=accessories&sub_category=scarf" },
        ],
      },
    ],
  },
  {
    label: "Unisex Wear",
    demographic: "unisex",
    columns: [
      {
        title: "Topwear",
        links: [
          { label: "T-Shirts", to: "/shop?demographic=unisex&category=tshirt" },
          { label: "Hoodies", to: "/shop?demographic=unisex&category=hoodie" },
          { label: "Polos", to: "/shop?demographic=unisex&category=polo" },
        ],
      },
      {
        title: "Accessories",
        links: [
          { label: "Caps", to: "/shop?demographic=unisex&category=accessories&sub_category=cap" },
          { label: "Watches", to: "/shop?demographic=unisex&category=accessories&sub_category=watch" },
          { label: "Sunglasses", to: "/shop?demographic=unisex&category=accessories&sub_category=sunglasses" },
        ],
      },
    ],
  },
  {
    label: "Couple Wear",
    demographic: "couple",
    columns: [
      {
        title: "Matching",
        links: [
          { label: "Matching T-Shirts", to: "/shop?demographic=couple&category=tshirt" },
          { label: "Matching Hoodies", to: "/shop?demographic=couple&category=hoodie" },
          { label: "Couple Mugs", to: "/shop?demographic=couple&category=drinkware&sub_category=couple_mug" },
        ],
      },
      {
        title: "Special",
        links: [
          { label: "Anniversary Combos", to: "/shop?demographic=couple&collection=anniversary" },
          { label: "Valentine's Picks", to: "/shop?demographic=couple&collection=valentine" },
        ],
      },
    ],
  },
  {
    label: "Ethnic Wear",
    columns: [
      {
        title: "Men",
        links: [
          { label: "Men's Kurta", to: "/shop?demographic=men&category=kurta" },
          { label: "Sherwani", to: "/shop?demographic=men&category=sherwani" },
        ],
      },
      {
        title: "Women",
        links: [
          { label: "Sarees", to: "/shop?demographic=women&category=saree" },
          { label: "Kurtis", to: "/shop?demographic=women&category=kurti" },
          { label: "Salwar Kameez", to: "/shop?demographic=women&category=salwar_kameez" },
          { label: "Lehenga", to: "/shop?demographic=women&category=lehenga" },
        ],
      },
      {
        title: "Kids",
        links: [
          { label: "Kids Ethnic", to: "/shop?demographic=kids&category=kurta" },
        ],
      },
    ],
  },
  {
    label: "Kids Wear",
    demographic: "kids",
    columns: [
      {
        title: "Topwear",
        links: [
          { label: "T-Shirts", to: "/shop?demographic=kids&category=tshirt" },
          { label: "Hoodies", to: "/shop?demographic=kids&category=hoodie" },
        ],
      },
      {
        title: "Occasion",
        links: [
          { label: "School Uniforms", to: "/shop?demographic=kids&category=uniform&sub_category=school" },
          { label: "Sports Wear", to: "/shop?demographic=kids&category=sports" },
          { label: "Ethnic", to: "/shop?demographic=kids&category=kurta" },
        ],
      },
    ],
  },
  {
    label: "Accessories",
    columns: [
      {
        title: "By Gender",
        links: [
          { label: "Men's Accessories", to: "/shop?demographic=men&category=accessories" },
          { label: "Women's Accessories", to: "/shop?demographic=women&category=accessories" },
          { label: "Unisex", to: "/shop?demographic=unisex&category=accessories" },
          { label: "Kids", to: "/shop?demographic=kids&category=accessories" },
        ],
      },
      {
        title: "Bags & Wallets",
        links: [
          { label: "Wallets", to: "/shop?category=accessories&sub_category=wallet" },
          { label: "Handbags", to: "/shop?category=accessories&sub_category=handbag" },
          { label: "Clutches", to: "/shop?category=accessories&sub_category=clutch" },
        ],
      },
      {
        title: "Style",
        links: [
          { label: "Sneakers", to: "/shop?category=accessories&sub_category=sneakers" },
          { label: "Sunglasses", to: "/shop?category=accessories&sub_category=sunglasses" },
          { label: "Caps", to: "/shop?category=accessories&sub_category=cap" },
          { label: "Watches", to: "/shop?category=accessories&sub_category=watch" },
        ],
      },
    ],
  },
  {
    label: "Drinkware & Lifestyle",
    highlight: true,
    columns: [
      {
        title: "Drinkware",
        links: [
          { label: "Ceramic Mugs", to: "/shop?category=drinkware&sub_category=mug" },
          { label: "Magic Mugs", to: "/shop?category=drinkware&sub_category=magic_mug" },
          { label: "Couple Mugs", to: "/shop?category=drinkware&sub_category=couple_mug" },
          { label: "Steel Bottles", to: "/shop?category=drinkware&sub_category=bottle" },
          { label: "Sippers", to: "/shop?category=drinkware&sub_category=sipper" },
        ],
      },
      {
        title: "Personalized Gifts",
        links: [
          { label: "Mousepads", to: "/shop?category=lifestyle&sub_category=mousepad" },
          { label: "Keychains", to: "/shop?category=lifestyle&sub_category=keychain" },
          { label: "Phone Cases", to: "/shop?category=lifestyle&sub_category=phone_case" },
          { label: "Photo Frames", to: "/shop?category=lifestyle&sub_category=photo_frame" },
          { label: "Cushions", to: "/shop?category=lifestyle&sub_category=cushion" },
        ],
      },
    ],
  },
  {
    label: "Sports",
    columns: [
      {
        title: "Apparel",
        links: [
          { label: "Jerseys", to: "/shop?category=sports&sub_category=jersey" },
          { label: "Track Suits", to: "/shop?category=sports&sub_category=tracksuit" },
          { label: "Gym Wear", to: "/shop?category=sports&sub_category=gym_wear" },
          { label: "Shorts", to: "/shop?category=sports&sub_category=shorts" },
        ],
      },
    ],
  },
  {
    label: "Uniforms",
    columns: [
      {
        title: "By Type",
        links: [
          { label: "School", to: "/shop?category=uniform&sub_category=school" },
          { label: "Corporate", to: "/shop?category=uniform&sub_category=corporate" },
          { label: "Industrial", to: "/shop?category=uniform&sub_category=industrial" },
          { label: "Medical / Scrubs", to: "/shop?category=uniform&sub_category=medical" },
          { label: "Hospitality / Chef", to: "/shop?category=uniform&sub_category=hospitality" },
        ],
      },
    ],
  },
  {
    label: "Corporates",
    columns: [
      {
        title: "Bulk Apparel",
        links: [
          { label: "Bulk Polos", to: "/shop?category=corporate&sub_category=bulk_polo" },
          { label: "Formal Shirts", to: "/shop?category=corporate&sub_category=formal_shirt" },
          { label: "Blazers", to: "/shop?category=corporate&sub_category=blazer" },
          { label: "Ties", to: "/shop?category=corporate&sub_category=tie" },
        ],
      },
      {
        title: "Corporate Gifts",
        links: [
          { label: "Branded Mugs", to: "/shop?category=drinkware&collection=corporate-collection" },
          { label: "Branded Bottles", to: "/shop?category=drinkware&sub_category=bottle&collection=corporate-collection" },
          { label: "Branded Diaries", to: "/shop?category=lifestyle&sub_category=diary" },
        ],
      },
    ],
  },
];

// Custom Shop sidebar filter — top-level demographic + special groups (Accessories, Drinkware).
// Default selected = "men". Each option ultimately filters by demographic OR category.
// Labels are kept SHORT so the sidebar doesn't wrap into 2 lines — the
// sub-categories shown underneath (when selected) already describe the depth.
export const CUSTOM_SHOP_FILTERS = [
  { value: "men", label: "Men's Wear", kind: "demographic" },
  { value: "women", label: "Women's Wear", kind: "demographic" },
  { value: "unisex", label: "Unisex Wear", kind: "demographic" },
  { value: "couple", label: "Couple Wear", kind: "demographic" },
  { value: "ethnic-group", label: "Ethnic Wear", kind: "category_group", categories: ["saree","kurta","kurti","salwar_kameez","lehenga","sherwani"] },
  { value: "kids", label: "Kids Wear", kind: "demographic" },
  { value: "accessories", label: "Accessories", kind: "category" },
  { value: "drinkware", label: "Drinkware", kind: "category" },
  { value: "lifestyle", label: "Lifestyle", kind: "category" },
  { value: "sports", label: "Sports Wear", kind: "category" },
  { value: "uniform", label: "Uniforms", kind: "category" },
  { value: "corporate", label: "Corporates", kind: "category" },
];

export function labelForDemographic(v) {
  return DEMOGRAPHICS.find((d) => d.value === v)?.label || v;
}
export function labelForCategory(v) {
  return CATEGORIES.find((c) => c.value === v)?.label || v;
}
export function labelForSubCategory(cat, v) {
  return (SUB_CATEGORIES[cat] || []).find((s) => s.value === v)?.label || v;
}

// ============================================================================
// CATEGORY_FILTERS — declares WHICH filter chips appear in the Catalog left
// sidebar for each main category. Backend `/api/catalog/filters` returns the
// values present in scope; the frontend renders only the slots listed here
// for the current `category` (in the order defined).
// 
// Filter keys:
//   "size"          → product.available_sizes union (chip pills)
//   "color"         → product.available_colors union (swatch grid)
//   "fit_type"      → product.fit_type union
//   "price"         → product.base_price/sale_price min-max slider
//   "discount"      → discount tier chips (10%+/25%+/50%+)
//   "rating"        → rating bucket chips (3+/4+)
//   "spec:KEY"      → product.specifications[KEY] union (free-form admin specs)
// ============================================================================
export const CATEGORY_FILTERS = {
  tshirt:        ["size", "fit_type", "spec:sleeve", "spec:neck", "color", "spec:fabric", "spec:gsm", "price", "discount", "rating"],
  shirt:         ["size", "fit_type", "spec:sleeve", "spec:pattern", "color", "spec:fabric", "price", "discount", "rating"],
  hoodie:        ["size", "fit_type", "spec:hood_type", "color", "spec:fabric", "price", "discount", "rating"],
  polo:          ["size", "fit_type", "color", "spec:fabric", "price", "discount", "rating"],
  tank:          ["size", "fit_type", "color", "spec:fabric", "price", "discount", "rating"],
  // Ethnic
  saree:         ["spec:fabric", "spec:work", "spec:occasion", "color", "price", "discount", "rating"],
  kurta:         ["size", "spec:fabric", "spec:work", "color", "price", "discount", "rating"],
  kurti:         ["size", "spec:fabric", "spec:work", "color", "price", "discount", "rating"],
  salwar_kameez: ["size", "spec:fabric", "spec:work", "color", "price", "discount", "rating"],
  lehenga:       ["size", "spec:fabric", "spec:work", "spec:occasion", "color", "price", "discount", "rating"],
  sherwani:      ["size", "spec:fabric", "spec:work", "color", "price", "discount", "rating"],
  // Activity wear
  sports:        ["size", "spec:sport", "color", "spec:fabric", "price", "discount", "rating"],
  uniform:       ["size", "spec:uniform_type", "color", "spec:fabric", "price", "discount", "rating"],
  corporate:     ["size", "fit_type", "color", "spec:fabric", "price", "discount", "rating"],
  // Accessories & lifestyle
  accessories:   ["color", "spec:material", "price", "discount", "rating"],
  drinkware:     ["spec:capacity", "spec:material", "spec:theme", "color", "price", "discount", "rating"],
  lifestyle:     ["spec:theme", "spec:material", "spec:size", "color", "price", "discount", "rating"],
  footwear:      ["spec:size_uk", "spec:type", "color", "spec:material", "price", "discount", "rating"],
  bag:           ["spec:type", "spec:capacity", "color", "spec:material", "price", "discount", "rating"],
};

// Friendly labels for spec facet keys — kept simple so any new spec key in
// admin product specs falls back to its raw key (capitalised).
export const SPEC_FACET_LABELS = {
  sleeve: "Sleeve",
  neck: "Neck",
  pattern: "Pattern",
  hood_type: "Hood Type",
  fabric: "Fabric",
  gsm: "GSM",
  work: "Work",
  occasion: "Occasion",
  sport: "Sport",
  uniform_type: "Uniform Type",
  material: "Material",
  capacity: "Capacity",
  theme: "Theme",
  size: "Size Variant",
  size_uk: "UK Size",
  type: "Type",
  customization: "Customization",
  approval: "Approval",
};

export function labelForSpecFacet(key) {
  if (SPEC_FACET_LABELS[key]) return SPEC_FACET_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Default filter set for unknown / no-category state (fallback)
export const DEFAULT_FILTERS = ["size", "color", "fit_type", "price", "discount", "rating"];
